import React, { useEffect, useMemo, useState } from 'react';
import {
  formatMoney, signupLabel, measurementInfo, CART_TABS, cartStatusLabel, cartTabCounts,
  filterCartsByTab, defaultCartTab, visibleAddresses, addressPlace,
} from './cartAnalyticsFormat';

const line = (v) => (v === null || v === undefined || v === '' ? '-' : v);

const STATUS_CLASS = {
  INCOMPLETE: 'ca-status-incomplete',
  COMPLETED: 'ca-status-completed',
  SAVE_LATER: 'ca-status-save-later',
};

function StatusBadge({ state }) {
  return <span className={`ca-status-badge ${STATUS_CLASS[state] || 'ca-status-other'}`}>{cartStatusLabel(state)}</span>;
}

function Thumb({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <div className="ca-line-thumb ca-line-thumb-empty" aria-hidden="true">No image</div>;
  return <img className="ca-line-thumb" src={src} alt={alt || ''} loading="lazy" onError={() => setFailed(true)} />;
}

function CartLine({ l, info }) {
  const m = measurementInfo(info);
  const offer = l.offer_active === 1 || l.offer_active === '1';
  // Product description from the line view (family title, then the code, only when there is none);
  // item id from the info view's Measurement step.
  const title = l.product_desc || l.sfp_title || l.sol_pr_item_code;
  return (
    <div className="ca-line-item">
      <Thumb src={l.image_url} alt={title} />
      <div className="ca-line-desc">
        <p className="ca-line-name">
          {line(title)}
          {offer && <span className="ca-offer-badge">Below old price</span>}
        </p>
        {(m.itemId || l.sol_item_label) && (
          <p className="ca-line-codes">
            {m.itemId && <span>Item ID <strong>{m.itemId}</strong></span>}
            {l.sol_item_label && <span>{l.sol_item_label}</span>}
          </p>
        )}
        {m.size && (
          <p className="ca-line-opt">
            <span className="ca-line-opt-label">Measurement</span>
            <span className="ca-line-opt-value">{m.size}</span>
          </p>
        )}
        {m.options.map((o, idx) => (
          <p key={`${o.label}-${idx}`} className="ca-line-opt">
            <span className="ca-line-opt-label">{o.label}</span>
            <span className="ca-line-opt-value">{o.value || '-'}</span>
          </p>
        ))}
      </div>
      <div className="ca-line-price">
        <span className="ca-line-qty-text">{l.sol_qty} × {formatMoney(l.sol_price, l.sol_ccy_code)}</span>
        {offer && l.sol_old_price > 0 && <span className="ca-line-old">{formatMoney(l.sol_old_price, l.sol_ccy_code)}</span>}
        <span className="ca-line-total">{formatMoney(l.line_value, l.sol_ccy_code)}</span>
      </div>
    </div>
  );
}

export default function CustomerDetail({ data, loading, error, piiVisible, onClose }) {
  const customer = data && data.customer;
  const session = data && data.session;
  const location = data && data.location;
  const addresses = (data && data.address) || [];
  const carts = useMemo(() => (data && data.carts) || [], [data]);
  const lines = (data && data.lines) || [];
  const info = (data && data.info) || [];
  const head = (data && data.head) || [];

  const infoBySol = info.reduce((m, i) => {
    (m[i.soi_sol_sys_id] = m[i.soi_sol_sys_id] || []).push(i);
    return m;
  }, {});
  const headBySoh = head.reduce((m, h) => { m[h.soh_sys_id] = h; return m; }, {});

  const counts = useMemo(() => cartTabCounts(carts), [carts]);
  const [tab, setTab] = useState(defaultCartTab(counts));
  const [allAddresses, setAllAddresses] = useState(false);
  // A different customer starts on its first non-empty tab with the address list collapsed.
  useEffect(() => { setTab(defaultCartTab(counts)); setAllAddresses(false); }, [counts]);

  const shownCarts = filterCartsByTab(carts, tab);
  const { visible: shownAddresses, hidden: hiddenAddresses } = visibleAddresses(addresses, allAddresses);

  return (
    <div className="ca-detail-backdrop" onClick={onClose}>
      <aside className="ca-detail" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="ca-detail-topbar">
          <h5 className="ca-detail-heading">Customer detail</h5>
          <button type="button" className="btn btn-sm btn-outline-secondary ca-detail-close" onClick={onClose}>Close</button>
        </div>

        {loading && <div className="ca-loading mt-3">Loading…</div>}
        {error && <div className="alert alert-danger mt-3">{error}</div>}

        {!loading && !error && data && (
          <div>

            {/* ── Profile ── */}
            <section className="ca-detail-section">
              <p className="ca-detail-section-title">Profile</p>
              {customer ? (
                <dl className="ca-detail-dl">
                  <div className="ca-detail-dl-row"><dt>Name</dt><dd>{line(customer.name)}</dd></div>
                  <div className="ca-detail-dl-row"><dt>Sign-up</dt><dd>{signupLabel(customer.signup_method)} · {line(customer.signup_date)}</dd></div>
                  {piiVisible && <div className="ca-detail-dl-row"><dt>Email</dt><dd>{line(customer.email)}</dd></div>}
                  {piiVisible && <div className="ca-detail-dl-row"><dt>Mobile</dt><dd>{line(customer.mobile)}</dd></div>}
                </dl>
              ) : (
                <p className="ca-detail-muted">Guest visitor — not signed in.</p>
              )}
              <p className="ca-detail-session">
                {location && (location.city || location.country)
                  ? [location.city, location.country].filter(Boolean).join(', ')
                  : 'Unknown location'}
                &ensp;·&ensp;
                {session ? [session.device, session.browser].filter(Boolean).join(' / ') : 'Unknown device'}
              </p>
              {!piiVisible && (
                <div className="ca-pii-notice mt-2">
                  <span>&#128274;</span>
                  Contact details are hidden for your role.
                </div>
              )}
            </section>

            {/* ── Addresses ── */}
            {addresses.length > 0 && (
              <section className="ca-detail-section">
                <p className="ca-detail-section-title">Addresses <span className="ca-detail-count">{addresses.length}</span></p>
                {shownAddresses.map((a) => (
                  <div key={a.cad_sys_id} className="ca-address-card">
                    <div className="ca-address-header">
                      <span className="ca-address-type">{line(a.cad_address_type)}</span>
                      {a.cad_default_yn === 'Y' && <span className="ca-address-default">default</span>}
                    </div>
                    <p className="ca-address-line">{addressPlace(a)}</p>
                    {piiVisible && (
                      <p className="ca-address-detail">
                        {[a.cad_building_name_no, a.cad_floor_no && `Floor ${a.cad_floor_no}`, a.cad_apartment_no && `Apt ${a.cad_apartment_no}`, a.cad_street_name_no, a.cad_nearest_landmark, a.cad_postal_code].filter(Boolean).join(', ')}
                        {[a.cad_contact_number, a.cad_phone_number].filter(Boolean).length > 0 && (
                          <span> &middot; {[a.cad_contact_number, a.cad_phone_number].filter(Boolean).join(' / ')}</span>
                        )}
                      </p>
                    )}
                  </div>
                ))}
                {(hiddenAddresses > 0 || allAddresses) && addresses.length > 2 && (
                  <button type="button" className="btn btn-link btn-sm ca-address-toggle" onClick={() => setAllAddresses(!allAddresses)}>
                    {allAddresses ? 'Show less' : `Show ${hiddenAddresses} more`}
                  </button>
                )}
              </section>
            )}

            {/* ── Carts ── */}
            <section className="ca-detail-section">
              <p className="ca-detail-section-title">Carts <span className="ca-detail-count">{carts.length}</span></p>
              {carts.length === 0 && <p className="ca-detail-muted">No carts found.</p>}
              {carts.length > 0 && (
                <ul className="ca-cart-tabs" role="tablist">
                  {CART_TABS.map((t) => (
                    <li key={t.key}>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        className={`ca-cart-tab ${tab === t.key ? 'active' : ''}`}
                        onClick={() => setTab(t.key)}
                      >
                        {t.label} <span className="ca-cart-tab-count">{counts[t.key]}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {carts.length > 0 && shownCarts.length === 0 && <p className="ca-detail-muted">No {cartStatusLabel(tab).toLowerCase()} carts.</p>}
              {shownCarts.map((c) => {
                const cartLines = lines.filter((l) => l.cart_id === c.cart_id);
                const h = c.soh_sys_id ? headBySoh[c.soh_sys_id] : null;
                const extras = h ? [
                  h.soh_promo_code && `Promo: ${h.soh_promo_code}`,
                  (h.carrier_desc && h.carrier_desc !== 'None') && `Delivery: ${h.carrier_desc}`,
                  (h.showroom_desc || h.soh_showroom_code) && `Showroom: ${h.showroom_desc || h.soh_showroom_code}`,
                  h.soh_ship_value ? `Shipping: ${formatMoney(h.soh_ship_value, c.ccy)}` : null,
                ].filter(Boolean) : [];

                return (
                  <div key={c.cart_id} className="ca-cart-card">
                    <div className="ca-cart-header">
                      <div>
                        <StatusBadge state={c.state} />
                        <p className="ca-cart-meta">Updated {c.last_updated_at}</p>
                        {extras.length > 0 && <p className="ca-cart-extras">{extras.join(' · ')}</p>}
                      </div>
                      <div className="ca-cart-value-block">
                        <span className="ca-cart-total">{formatMoney(c.cart_value, c.ccy)}</span>
                        <span className="ca-cart-qty">{c.items_qty} item{c.items_qty !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    {cartLines.length > 0 && (
                      <div className="ca-cart-body">
                        {cartLines.map((l) => <CartLine key={l.sol_sys_id} l={l} info={infoBySol[l.sol_sys_id]} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

          </div>
        )}
      </aside>
    </div>
  );
}
